/**
 * @module lib/election/live-results
 */
import { getProp, qs } from "utils/utils";
import ee from "lib/eventemitter";
import { getEntriesByName, mark, measure } from "../performance/user-timing";
import logger from "utils/logger";
import { parseDataSetObject } from "utils/utils";
import { compareResults, getStatus } from "lib/election/lead-data-helpers";
import tgamConfig from "config/tgam";

const debugTag = "[ELECTION-LIVE-RESULTS]";
const withinMajorityRangeClass = "c-election-live-results--majority-range";
let container = null; // live results feature container

// EDT or EST: https://www.timeanddate.com/time/zones/edt
// `Initial results starting at 7:30 p.m. <abbr title='Eastern Daylight Time'>EDT</abbr>`
const liveResultsSubheading = getProp(tgamConfig, "electionLiveResultsSubheading", "Initial results starting soon");

// In Election 2019, PPC was returned as "PPC" in the CP feed.  In 2021, it's returned as "PP".
let partyMappings = {
  LIB: {
    name: "Liberal",
    abbr: "lib",
    modifier: "lib",
  },
  CON: {
    name: "Conservative",
    abbr: "con",
    modifier: "cpc"
  },
  NDP: {
    name: "NDP",
    abbr: "ndp",
    modifier: "ndp"
  },
  BQ: {
    name: "Bloc Québécois",
    abbr: "bq",
    modifier: "bq"
  },
  GRN: {
    name: "Green",
    abbr: "grn",
    modifier: "grn"
  },
  PP: {
    name: "People's",
    abbr: "ppc",
    modifier: "ppc"
  },
  PPC: {
    name: "People's",
    abbr: "ppc",
    modifier: "ppc"
  },
  Others: {
    name: "Others",
    abbr: "oth",
    modifier: "oth"
  }
};

/**
 * @method init
 * @description Initialize the live results component.
 */

export function initElectionLiveResults() {
  container = qs(".js-election-live-results");

  if (!container) {
    return;
  }

  mark("live-results_started");

  const shouldOverrideData = container.dataset.override === "true";

  ee.on("election:leading-data-failed", () => {
    logger.info(`${debugTag} Data failed to load: overriding`);
    overrideData();
  });

  if (shouldOverrideData) { // Use manually overridden data instead of feed data.
    overrideData();
  }

  ee.on("election:leading-data", data => {
    if (shouldOverrideData || !data) {
      logger.info(`${debugTag} exited`);
      return;
    }

    logger.info(`${debugTag} Data`, data);

    const status = getStatus(data);

    // Make a copy of the array since we'll be adding an element to it for testing purposes.
    // We don't want to actually alter "data".
    let leadingElectedPartyData = [...getProp(data, "Leading.Party", [])];

    // Sort the data from highest to lowest number of seats.
    leadingElectedPartyData.sort((resultA, resultB) => compareResults(resultA, resultB));

    logger.info(`${debugTag} leadingElectedPartyData `, leadingElectedPartyData);

    mark("live-results_started");

    updatePollPercentageBlurb(getProp(data, "PercentageOfPollsReporting", ""), status);

    populateSeats(leadingElectedPartyData, status, data.TotalRidings);

    mark("live-results_done");
    measure("live-results");
    logger.info(`${debugTag} Performance measure result:`, getEntriesByName("live-results"));
  });
}

/**
 * @method overrideData
 * @description Populates the feature with the override data when needed
 */
function overrideData() {
  // We can assume that if data is overridden, status is partial. Just allowing for no results status as well for testing purposes.
  let status = "";
  let overrideValues = parseDataSetObject(container.dataset.overridevalues);
  let seeAllText = qs(".c-election-live-results__full-results");
  if (overrideValues) {
    overrideValues = overrideValues.Items.sort((resultA, resultB) => compareResults(resultA, resultB));
    logger.info(`${debugTag} overrideValues`, overrideValues);
  }
  if (seeAllText) {
    seeAllText.classList.add("u-hidden");
  }

  // For purposes of the overridden data scenario, we can assume that the status is partial.
  // No need for the "complete" status.  The colours are the same.
  overrideValues.forEach((partyResult) => {
    if (getProp(partyResult, "LeadingElected", 0) > 0) {
      status = "partial";
    }
  });

  populateSeats(overrideValues, status);

  hidePollPercentageBlurb();

  mark("live-results_done");
  measure("live-results");
  logger.info(`${debugTag} Performance measure result (manual override scenario):`, getEntriesByName("live-results"));
}


/**
 * Populates leading + elected results numbers for all parties.
 * @param {Array<Object>} leadingElectedData - array of JSON objects with leading and elected party data, from highest to lowest
 * @param {String} status - status of election (i.e., "" for no results, "partial", and "complete")
 * @param {Number} totalRidings - number of ridings in the election
 */
function populateSeats(leadingElectedData, status, totalRidings = 338) {
  const livePartyResultsList = qs(".c-election-live-results__parties");
  const barChart = qs(".c-election-live-results__chart");
  const rainbowChart = qs(".c-election-live-results__rainbow");

  let listItems = "";
  let barChartItems = "";
  let rainbowChartItems = "";
  let isWithinRange = false;

  if (status) { // Partial or complete results.  Show chart and line of majority.
    const majorityLine = qs(".c-election-live-results__majority--chart");

    container.classList.add(`c-election-live-results--${status}`);

    if (majorityLine) {
      majorityLine.classList.remove("u-hidden");
    }

    if (barChart) {
      barChart.classList.remove("u-hidden");
    }

    if (rainbowChart) {
      rainbowChart.classList.remove("u-hidden");
    }
  }

  logger.info(`${debugTag} leadingElectedData:`, leadingElectedData);

  leadingElectedData.forEach((partyResult) => {
    const leadingElectedCount = getProp(partyResult, "LeadingElected", 0);
    const partyInfo = partyMappings[partyResult.Name];

    if (partyInfo) {
      listItems += getPartyResultItem(partyInfo.abbr, partyInfo.modifier, partyInfo.name, leadingElectedCount);

      if (barChart) {
        barChartItems += getBarChartItem(partyInfo.modifier, leadingElectedCount);
      }

      if (rainbowChart) {
        rainbowChartItems += getRainbowChartItem(partyInfo.modifier, totalRidings, leadingElectedCount);
      }
    }

    // Only bother calling isWithinMajorityRange() if the previous results processed were not within
    // the majority range.
    if (!isWithinRange) {
      isWithinRange = isWithinMajorityRange(leadingElectedCount);
    }
  });

  if (livePartyResultsList) {
    livePartyResultsList.innerHTML = listItems;
  }

  if (isWithinRange && !container.classList.contains(withinMajorityRangeClass)) {
    // Remove extra padding under the line of majority.
    container.classList.add(withinMajorityRangeClass);
  }

  if (barChart) {
    barChart.innerHTML = barChartItems;
  }

  if (rainbowChart) {
    rainbowChart.innerHTML = rainbowChartItems;
  }
}

/**
 * Returns leading + elected party result list item, consisting of party name and number of seats.
 * @param {String} abbr - abbreviated version of party name
 * @param {String} modifier - CSS colour class modifier
 * @param {String} partyName - party name
 * @param {Number} [seats=0] - number of "seats" (i.e., leading + elected)
 * @returns {String} list item for live results
 */
function getPartyResultItem(abbr, modifier, partyName, seats = 0) {
  return `<li class="c-election-live-results__party c-election-live-results__party--${modifier}">
    <div class="c-election-live-results__party-name--full">${partyName}</div>
    <abbr title="${partyName}" class="c-election-live-results__party-name--abbr">${abbr}</abbr>
    <div class="c-election-live-results__party-seats">${seats}</div>
  </li>`;
}

/**
 * Updates percentage of polls reported blurb on live results feature.
 * @param {Number} percentageOfPollsReported - percentage of polls reported
 * @param {String} status - status of election (i.e., "" for no results, "partial", or "complete")
 */
function updatePollPercentageBlurb(percentageOfPollsReported, status) {
  let blurb = liveResultsSubheading; // Default no results text.
  const blurbElement = qs(".c-election-live-results-heading__sub");

  // There may be cases when the election has started but polls reported is 0% (due to rounding of values like 0.6%).
  // The business is okay with showing "0% polls reported", as it would only show for a short period anyways.
  if (status === "complete" || status === "partial") {
    blurb = `${percentageOfPollsReported}% polls reported`;
  }

  if (blurbElement) {
    blurbElement.innerHTML = blurb;
  }
}

/**
 * Returns bar chart item representing party result.
 * @param {String} partyModifier - party's abbreviated name
 * @param {Number} [numSeats=0] - number of "seats" (i.e., leading + elected)
 * @returns {String} HTML for barchart item representing party result
 */
function getBarChartItem(partyModifier, numSeats = 0) {
  return `<div class="c-election-live-results__chart-item c-election-live-results__chart-item--${partyModifier}" style="height:${numSeats}px"></div>`;
}

/**
 * Returns rainbow chart item representing party result.
 * @param {String} partyModifier - party's abbreviated name
 * @param {Number} totalSeats - total seats/ridings in the election
 * @param {Number} [numSeats=0] - number of "seats" (i.e., leading + elected)
 * @returns {String} HTML for rainbow chart item representing party result
 */
function getRainbowChartItem(partyModifier, totalSeats, numSeats = 0) {
  const widthPercentage = numSeats / totalSeats * 100;
  return `<div class="c-election-live-results__rainbow__${partyModifier}" style="width:${widthPercentage}%"></div>`;
}

/**
 * Returns whether or not numSeats is within the majority range.
 * @param {Number} numSeats - number of leading + elected seats for party
 * @param {Number} [totalRidings = 338] - total ridings/seats
 * @param {Number} [range = 60] - number of seats from majority line when padding under it gets removed
 * @returns {Boolean}
 */
function isWithinMajorityRange(numSeats, totalRidings = 338, range = 60) {
  const majority = Math.round(totalRidings / 2) + 1;
  return numSeats >= (majority - range);
}

/**
 * Hides poll percentage blurb
 */
function hidePollPercentageBlurb() {
  const blurbElement = qs(".c-election-live-results-heading__sub");

  if (blurbElement) {
    blurbElement.classList.add("u-hidden");
  }
}
